import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  AlternativeCartridgeVariantOwnersResponseDto,
  Auth0Enrollment,
  CartridgeUserDTO,
  DeactivateOrganizationMembershipsRequestDto,
  DeactivateOrganizationMembershipsResponseDto,
  MembershipOriginInformation,
  OrganizationUserDTO,
  UpdateUserLanguageRequestDto,
  UpdateUserLanguageResponseDto,
  User,
  UserDTO,
  UserGuidedTourDTO,
  UserInviteToOrganizationDto,
  UserNamesDTO,
  UserOrganizationStatus,
  UserUpdateDTO,
} from '@markant-platform/shared/user/model';
import { catchError, map, Observable, of, Subject, tap } from 'rxjs';
import { AppConfigService, AppFeature, buildParameters, Page } from '@markant-platform/shared/data-access';
import { SortAttributes } from '@markant-platform/shared/table/model';
import { OrganizationContact } from '@markant-platform/shared/organization/model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  apiUrl: string = AppConfigService.appConfiguration.apiUrl['userService'];
  userOrganizationApiUrl: string = AppConfigService.appConfiguration.apiUrl['userOrganization'];
  updatedUser$: Subject<UserUpdateDTO> = new Subject<UserUpdateDTO>();

  constructor(private readonly http: HttpClient) {}

  patchOrganizationStatus(): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/organization-membership/status`, {});
  }

  updateUserForOrganization(
    userId: string,
    organizationId: string | undefined,
    userUpdateDTO: UserUpdateDTO,
    lastChangeReason?: string
  ): Observable<UserUpdateDTO> {
    if (organizationId) {
      userUpdateDTO.organizationId = organizationId;
    }
    if (lastChangeReason) {
      userUpdateDTO.reason = lastChangeReason;
    }

    return this.http
      .put<UserUpdateDTO>(`${this.apiUrl}/${userId}`, userUpdateDTO)
      .pipe(tap(userUpdate => this.updatedUser$.next(userUpdate)));
  }

  patchUserLanguage(
    userId: string,
    updateUserLanguageRequestDto: UpdateUserLanguageRequestDto
  ): Observable<UpdateUserLanguageResponseDto> {
    return this.http.patch<UserUpdateDTO>(`${this.apiUrl}/${userId}/language`, updateUserLanguageRequestDto);
  }

  getUserForOrganization(userId: string, organizationId: string): Observable<OrganizationUserDTO> {
    return this.http
      .get<UserDTO>(`${this.apiUrl}/${userId}?organization-id=${organizationId}`)
      .pipe(map(user => this.mapUserDtoToUserOrganizationDto(user, organizationId)));
  }

  getUser(userId: string): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${userId}`);
  }

  getOrganizationAdmins(organizationId: string): Observable<OrganizationContact[]> {
    return this.http.get<OrganizationContact[]>(`${this.userOrganizationApiUrl}/${organizationId}/admins`);
  }

  getMembersOfSubOrganization(
    parentOrganizationId: string,
    subOrganizationId: string,
    index?: number,
    size?: number,
    sortDirection?: 'asc' | 'desc',
    search?: string
  ): Observable<Page<OrganizationUserDTO>> {
    const sortAttributes = { sortOrder: sortDirection } as SortAttributes;
    return this.getUsers(
      parentOrganizationId,
      subOrganizationId,
      { index: index, size: size, sort: sortAttributes },
      search
    ).pipe(map(pagedUserDto => this.mapPagesUserDtoToOrganizationUserDto(pagedUserDto, parentOrganizationId)));
  }

  getUsersInOrganizationAsOrganizationUserDto(
    organizationId: string,
    index?: number,
    size?: number,
    sort?: SortAttributes,
    query?: string,
    filter?: string,
    includePendingUser?: boolean
  ): Observable<Page<OrganizationUserDTO>> {
    return this.getUsers(
      organizationId,
      undefined,
      { index: index, size: size, sort: sort },
      query,
      filter,
      includePendingUser
    ).pipe(map(pagedUserDto => this.mapPagesUserDtoToOrganizationUserDto(pagedUserDto, organizationId)));
  }

  getUsers(
    organizationId?: string,
    suborganizationId?: string,
    page?: { index?: number; size?: number; sort?: SortAttributes },
    query?: string,
    filter?: string,
    includePendingUser?: boolean,
    excludesEmailMatching?: boolean,
    includeDeletedUser?: boolean
  ): Observable<Page<UserDTO>> {
    let params = buildParameters({
      'organization-id': organizationId,
      'suborganization-id': suborganizationId,
      'include-pending': includePendingUser,
      'include-deleted': includeDeletedUser,
      'sort-field': page?.sort?.sortField,
      'sort-order': page?.sort?.sortOrder,
      query: query,
      fields: excludesEmailMatching ? ['FIRSTNAME', 'LASTNAME'] : undefined,
      index: page?.index,
      size: page?.size,
    });

    const _filter: any = new URLSearchParams(filter);
    for (const [key, value] of _filter) {
      params = params.append(key, value);
    }

    return this.http.get<Page<UserDTO>>(`${this.apiUrl}`, { params: params });
  }

  getUsersForRole(
    organizationId: string,
    roleInternalName: string,
    roleCartridgeId?: string,
    active = true
  ): Observable<UserDTO[]> {
    const roleInternalNameParam: string = roleInternalName ? `&role=${roleInternalName}` : '';
    const roleCartridgeIdParam: string = roleCartridgeId ? `&cartridge-id=${roleCartridgeId}` : '';
    const activeUsersParam: string = active ? '&state=ACTIVE' : '';

    return this.http
      .get<
        Page<UserDTO>
      >(`${this.apiUrl}?organization-id=${organizationId}${roleInternalNameParam}${roleCartridgeIdParam}${activeUsersParam}`)
      .pipe(map(page => page.result));
  }

  getAdminsForCartridge(roleInternalName: string, cartridgeId: string): Observable<UserDTO[]> {
    return this.http
      .get<Page<UserDTO>>(`${this.apiUrl}?role=${roleInternalName}&cartridge-id=${cartridgeId}`)
      .pipe(map(page => page.result));
  }

  getSpecificUsers(organizationId: string, userIds: string[]): Observable<UserDTO[]> {
    return this.http.post<UserDTO[]>(`${this.apiUrl}s/`, { organizationId: organizationId, userIds: userIds });
  }

  getEnrollments(userId: string | undefined): Observable<Auth0Enrollment[]> {
    return this.http.get<Auth0Enrollment[]>(`${this.apiUrl}/${userId}/enrollment`);
  }

  deleteEnrollment(userId: string | undefined, enrollmentId: string): Observable<void> {
    const base64EncodedEnrollmentId = this.base64Encode(enrollmentId);

    return this.http.delete<void>(`${this.apiUrl}/${userId}/enrollment/${base64EncodedEnrollmentId}`);
  }

  changeMfa(userId: string | undefined, toSetMfa: boolean): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${userId}/mfa`, {
      mfaFlag: toSetMfa,
    });
  }

  getCountOfUsersInOrganization(organizationId: string): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/count?organization-id=${organizationId}`);
  }

  getCartridgeUsers(
    cartridgeId: string,
    index: number,
    size: number,
    query: string,
    filter: string
  ): Observable<Page<CartridgeUserDTO>> {
    const queryParam: string = !query ? '' : `&query=${query}`;
    const filterParam: string = !filter ? '' : `&${filter}`;
    return this.http
      .get<
        Page<UserDTO>
      >(`${this.apiUrl}/?cartridge-id=${cartridgeId}&index=${index}&size=${size}${queryParam}${filterParam}`)
      .pipe(
        map(({ result, ...page }) => {
          const mappedResult = result.map(
            ({ id, salutation, firstname, lastname, email, userType, organizationMemberships }) => {
              const roles: string[] = [];
              organizationMemberships?.forEach(({ roles: oRoles }) => roles.push(...oRoles));
              return {
                id,
                salutation,
                firstname,
                lastname,
                email,
                userType,
                roles,
                organizationName:
                  organizationMemberships?.map(({ organizationName }) => organizationName).join(', ') || '',
                organizationId: organizationMemberships?.map(({ organizationId }) => organizationId).join(', ') ?? '',
              };
            }
          );

          return {
            ...page,
            result: mappedResult,
          };
        })
      );
  }

  inviteUser(userInvitationDto: UserInviteToOrganizationDto): Observable<{ users: UserDTO[] }> {
    return this.http.post<{ users: UserDTO[] }>(`${this.apiUrl}/invitation`, { invitations: [userInvitationDto] });
  }

  deactivateOrganizationMembership(
    organizationId: string,
    deactivateOrganizationMemberships: DeactivateOrganizationMembershipsRequestDto[],
    newOwners?: {
      userId: string;
      cartridgeVariantId: string;
    }[]
  ): Observable<DeactivateOrganizationMembershipsResponseDto> {
    return this.http.delete<DeactivateOrganizationMembershipsResponseDto>(`${this.apiUrl}/organization-membership`, {
      body: {
        organizationId: organizationId,
        deactivateOrganizationMemberships: deactivateOrganizationMemberships,
        cartridgeVariantOwnerReplacements: newOwners || [],
      },
    });
  }

  getUnassignedMembersForSubOrganization(
    parentOrganizationId: string,
    subOrganizationId: string,
    index: number,
    size: number,
    sortDirection: 'asc' | 'desc',
    search: string
  ): Observable<Page<OrganizationUserDTO>> {
    const pagingParam = `&index=${index}&size=${size}`;
    const unassignedParam = `&not-in-suborganization=${subOrganizationId}`;
    const sortParam = `&sort-order=${sortDirection}`;
    const searchParam = search ? `&query=${search}` : '';

    return this.http.get<Page<OrganizationUserDTO>>(
      `${this.apiUrl}?organization-id=${parentOrganizationId}${pagingParam}${searchParam}${unassignedParam}${sortParam}`
    );
  }

  assignMembersToSubOrganization(
    organizationId: string,
    subOrganizationId: string,
    membersToAssign: string[]
  ): Observable<string[]> {
    return this.http.post<string[]>(`${this.apiUrl}/suborganization-membership`, {
      organizationId: organizationId,
      suborganizationId: subOrganizationId,
      userIds: membersToAssign,
    });
  }

  removeMembersFromSubOrganization(
    mainOrganizationId: string,
    subOrganizationId: string,
    membersToRemove: { [userId: string]: number }
  ): Observable<string[]> {
    return this.http.delete<string[]>(`${this.apiUrl}/suborganization-membership`, {
      body: {
        organizationId: mainOrganizationId,
        suborganizationId: subOrganizationId,
        userIdToVersion: membersToRemove,
      },
    });
  }

  changeActiveOrganization(userId: string, organizationId: string): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${userId}/organization`, { organizationId: organizationId });
  }

  generateSupportToken(): Observable<{ token: string }> {
    return this.http.post<{ token: string }>(`${this.apiUrl}/support-token`, {});
  }

  getMembershipOrigin(userId: string, organizationId: string): Observable<MembershipOriginInformation> {
    return this.http.get<MembershipOriginInformation>(
      `${this.apiUrl}/${userId}/organization-membership/${organizationId}/origin`
    );
  }

  mfaSelect(
    selectedMfaMethod: string | undefined,
    sessionToken: string | undefined,
    redirectUri: string | undefined,
    state: string | undefined
  ): Observable<{
    redirectUri: string;
  }> {
    return this.http.post<{ redirectUri: string }>(`${this.apiUrl}/mfa-select`, {
      selectedMfaMethod: selectedMfaMethod,
      sessionToken: sessionToken,
      redirectUri: redirectUri,
      state: state,
    });
  }

  checkAlternativeCartridgeVariantOwners(
    organizationId: string,
    userIds: string[]
  ): Observable<AlternativeCartridgeVariantOwnersResponseDto> {
    if (!AppConfigService.isFeatureEnabled(AppFeature.CARTRIDGE_VARIANT_OWNER_REPLACEMENT)) {
      return of({
        owners: [],
        alternativeOwners: [],
      });
    }
    return this.http.post<AlternativeCartridgeVariantOwnersResponseDto>(
      `${this.userOrganizationApiUrl}/${organizationId}/alternative-cartridge-variant-owners`,
      { userIds: userIds }
    );
  }

  getUserNames(userIds: string[]): Observable<UserNamesDTO> {
    return this.http.post<UserNamesDTO>(`${this.apiUrl}/names`, { userIds: userIds });
  }

  resendInvitationMail(userId: string, organizationId: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${userId}/organization-membership/${organizationId}/invitation-mail`,
      {}
    );
  }

  resendVerificationEmail(userId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${userId}/send-verification-email`, {});
  }

  getGuidedTourProgress(userId: string, guidedTourIdentifier: string): Observable<UserGuidedTourDTO> {
    return this.http.get<UserGuidedTourDTO>(`${this.apiUrl}/${userId}/guided-tour/${guidedTourIdentifier}`).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          return of({ completedSections: [] });
        }
        throw err;
      })
    );
  }

  setGuidedTourProgress(
    userId: string,
    guidedTourIdentifier: string,
    completedSections: UserGuidedTourDTO
  ): Observable<UserGuidedTourDTO> {
    return this.http.put<UserGuidedTourDTO>(
      `${this.apiUrl}/${userId}/guided-tour/${guidedTourIdentifier}`,
      completedSections
    );
  }

  private base64Encode(input: string): string {
    return btoa(input);
  }

  private mapPagesUserDtoToOrganizationUserDto(
    pagedUserDto: Page<UserDTO>,
    organizationId: string
  ): Page<OrganizationUserDTO> {
    const newPage: Page<OrganizationUserDTO> = {
      hasNext: pagedUserDto.hasNext,
      pageIndex: pagedUserDto.pageIndex,
      pageSize: pagedUserDto.pageSize,
      totalCount: pagedUserDto.totalCount,
      result: [],
    };

    newPage.result = pagedUserDto.result.map(userDto => this.mapUserDtoToUserOrganizationDto(userDto, organizationId));
    return newPage;
  }

  private mapUserDtoToUserOrganizationDto(userDto: UserDTO, organizationId: string): OrganizationUserDTO {
    const membership = userDto.organizationMemberships?.find(
      memberShip => memberShip.organizationId === organizationId
    ) || {
      organizationId: '',
      organizationName: '',
      userOrganizationStatus: UserOrganizationStatus.DELETED,
      roles: [],
    };

    const { organizationMemberships: _, ...user } = userDto;

    return {
      ...user,
      ...membership,
      organizationId: organizationId,
      cartridgeRights: [],
    } as OrganizationUserDTO;
  }
}
